/**
 * ClientPortalRouter - Feature Flag Aware Routing for Client Portals
 * 
 * When CLIENT_PORTAL_UNIFIED is enabled:
 * - /upload/:reportId redirects to /portal/:token (with conversion)
 * - /clarify/:token redirects to /portal/:token
 * 
 * When disabled:
 * - Old routes work as before
 * 
 * This enables parallel testing of the unified portal.
 */

import React from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import ClientUploadPortal from '../../pages/ClientUploadPortal';
import ClientClarificationPortal from '../../pages/ClientClarificationPortal';
import UnifiedClientPortal from '../../pages/UnifiedClientPortal';

/**
 * Upload Portal Router
 * Routes /upload/:reportId to either legacy or unified portal
 */
export function UploadPortalRouter() {
  const useUnified = useFeatureFlag('CLIENT_PORTAL_UNIFIED');
  const { reportId } = useParams<{ reportId: string }>();
  
  if (useUnified && reportId) {
    // Redirect to unified portal with reportId as token
    // The backend portal endpoint can handle both tokens and reportIds
    return <Navigate to={`/portal/${reportId}?source=upload`} replace />;
  }
  
  // Legacy behavior
  return <ClientUploadPortal />;
}

/**
 * Clarification Portal Router
 * Routes /clarify/:token to either legacy or unified portal
 */
export function ClarificationPortalRouter() {
  const useUnified = useFeatureFlag('CLIENT_PORTAL_UNIFIED');
  const { token } = useParams<{ token: string }>();
  
  if (useUnified && token) {
    // Redirect to unified portal
    return <Navigate to={`/portal/${token}?source=clarify`} replace />;
  }
  
  // Legacy behavior
  return <ClientClarificationPortal />;
}

/**
 * Unified Portal - Always renders the unified version
 * This is the target for all client portal routes when flag is enabled
 */
export function UnifiedPortalRouter() {
  // Always use the unified portal on this route
  return <UnifiedClientPortal />;
}

export default {
  UploadPortalRouter,
  ClarificationPortalRouter,
  UnifiedPortalRouter,
};
